/**
 * 配信ファイル(`content/v{N}/` と公開版リリース。docs/04 §3・docs/20 §2.1)の型。
 *
 * もとは `tools/content-build/src/types.ts` にあった定義の一次情報をここへ移した
 * (tools 側は再エクスポート)。公開版のファイルは現行の `content/v1` と同じパス・同じ書式。
 */

export const CONTENT_SCHEMA_VERSION = 1;

// ── 単語・ステージ ───────────────────────────────────────

export interface WordMeaning {
  readonly en?: string;
  readonly ja?: string;
}

export interface WordAudio {
  readonly en?: string;
  readonly ja?: string;
}

export interface WordMedia {
  /** 代表アセット(後方互換。images/audios が無い語はこちらを使う)。 */
  readonly image?: string;
  readonly audio?: WordAudio;
  /** 多様提示の画像バリエーション(スタイル4種。docs/04 §3、docs/05 §2b)。 */
  readonly images?: readonly string[];
  /** 多様提示の音声バリエーション(英語のみ話者4種。docs/05 §3b)。 */
  readonly audios?: { readonly en?: readonly string[] };
}

/** content/v{N}/words/{set}.json の1エントリ。 */
export interface WordEntry {
  readonly id: string;
  readonly display: string;
  readonly reading: string;
  readonly ruby?: string;
  readonly meaning?: WordMeaning;
  readonly media?: WordMedia;
  readonly tags: readonly string[];
}

export interface WordFile {
  readonly schemaVersion: number;
  /** 単語セットの識別子(`en` | `ja-drill` など)。 */
  readonly set: string;
  /** reading の言語(タイピング対象の言語)。 */
  readonly locale: string;
  readonly words: readonly WordEntry[];
}

export type QuizMode = 'sequential' | 'random' | 'timeLimit' | 'fixedQuestionCount';

export interface StageQuiz {
  readonly mode: QuizMode;
  readonly repeatCount?: number;
  readonly timeLimit?: number;
  readonly questionLimit?: number;
}

export interface StageUnlock {
  readonly requires: readonly string[];
  readonly minStars: number;
}

export type StageLanguage = 'japanese' | 'english';

/** content/v{N}/stages/{language}.json の1エントリ。 */
export interface Stage {
  readonly id: string;
  readonly language: StageLanguage;
  readonly category: 'practice' | 'training';
  readonly section: string;
  readonly title: string;
  readonly titleSub: string;
  readonly wordIds: readonly string[];
  readonly quiz: StageQuiz;
  readonly viewImage: boolean;
  readonly voice: boolean;
  readonly starCoins: readonly number[];
  readonly unlock: StageUnlock;
  readonly gameType: 'standard';
}

export interface StageFile {
  readonly schemaVersion: number;
  readonly language: StageLanguage;
  /** この言語モードが参照する単語セット(words/{set}.json)。 */
  readonly wordSets: readonly string[];
  readonly stages: readonly Stage[];
}

// ── カタログ(アバターアイテム / ガチャ)────────────────────

/** レアリティの昇順(docs/02_game_design.md §7.2)。 */
export const RARITIES = ['N', 'R', 'SR', 'UR'] as const;
export type Rarity = (typeof RARITIES)[number];

/** 現行アバター(`legacy-emoji` リグ)のパーツスロット(docs/02_game_design.md §7.1)。 */
export const ITEM_SLOTS = ['hat', 'face', 'outfit', 'hand', 'background', 'effect'] as const;
export type ItemSlot = (typeof ITEM_SLOTS)[number];

/**
 * engine の `ScoreModifierType` と一致していなければならない。
 * ここに無い効果タイプはクライアントで黙って無視されるため validate で弾く。
 */
export const ITEM_EFFECT_TYPES = [
  'gaugeDecayReduction',
  'comboCapUp',
  'missForgive',
  'coinBonus',
] as const;
export type ItemEffectType = (typeof ITEM_EFFECT_TYPES)[number];

export interface ItemEffect {
  readonly type: ItemEffectType;
  readonly value: number;
}

/**
 * アイテムの出どころ(docs/12_challenge_ranking.md §3)。
 *
 * `gacha`(既定)     … ふつうのガチャで手に入るもの
 * `challenge`        … チャレンジのトロフィーコインでしか手に入らないもの(装備ゲートの対象外)
 */
export const ITEM_ORIGINS = ['gacha', 'challenge'] as const;
export type ItemOrigin = (typeof ITEM_ORIGINS)[number];

/** content/v1(移行前)の catalog/items.json の1エントリ。 */
export interface CatalogItem {
  readonly id: string;
  readonly slot: ItemSlot;
  readonly rarity: Rarity;
  readonly name: { readonly ja: string };
  /** アート未整備のあいだの暫定表現。画像ができたら `image` を足す。 */
  readonly emoji: string;
  /** 将来の差し替え用(相対パス)。未設定ならクライアントは emoji を使う。 */
  readonly image?: string;
  /** 出どころ。省略は `gacha`(既存アイテムを書き換えずに済ませるため)。 */
  readonly origin?: ItemOrigin;
  readonly effects: readonly ItemEffect[];
  readonly services: readonly string[];
}

export interface ItemFile {
  readonly schemaVersion: number;
  readonly items: readonly CatalogItem[];
}

export interface GachaPoolEntry {
  readonly itemId: string;
  readonly weight: number;
  /**
   * **排出できる月**(`YYYY-MM` の配列。docs/12 §3)。省略した枠は恒常(いつでも出る)。
   * 当月の限定枠が1つも無くても恒常枠だけで抽選が成立すること(検証で強制)。
   */
  readonly months?: readonly string[];
}

/**
 * ウォレットの通貨(docs/10 §4.3 / docs/13 §9.1)。
 *
 * **一次情報は `functions/src/types.ts` の `Currency`**(Firestore のフィールド名)。
 * ここはカタログを検証するための写しで、tools/content-build の catalog.test.ts が一致を強制する。
 */
export const GACHA_CURRENCIES = [
  'typing',
  'puzzle',
  'trophy',
  'math',
  'language',
  'science',
  'social',
  'life',
] as const;
export type GachaCurrency = (typeof GACHA_CURRENCIES)[number];

/** content/v1(移行前)の catalog/gachas.json の1エントリ。 */
export interface GachaDefinition {
  readonly id: string;
  readonly name: { readonly ja: string };
  /** 支払いに使う通貨(docs/10 §4.3)。 */
  readonly currency: GachaCurrency;
  /** 単発コスト。 */
  readonly cost: number;
  /** 連数ぶんのコスト(guarantee.count 回ぶん)。 */
  readonly cost10: number;
  /** レアリティ排出率。合計 1.0。 */
  readonly rarityRates: Readonly<Record<Rarity, number>>;
  /** 連数と保証レアリティ。 */
  readonly guarantee: { readonly count: number; readonly minRarity: Rarity };
  /** 重複時の自動コイン変換額。 */
  readonly duplicateConvertCoins: Readonly<Record<Rarity, number>>;
  readonly pool: readonly GachaPoolEntry[];
  /** 開催期間(ISO8601)。null は無期限。 */
  readonly period: { readonly from: string | null; readonly to: string | null };
}

export interface GachaFile {
  readonly schemaVersion: number;
  readonly gachas: readonly GachaDefinition[];
}

// ── content/v{N}/manifest.json ───────────────────────────

export interface ManifestFileEntry {
  readonly path: string;
  /** `learn` = カリキュラムパックの宣言(docs/13 §9)。 */
  readonly kind: 'words' | 'stages' | 'catalog' | 'quiz' | 'learn';
  readonly count: number;
  readonly bytes: number;
  readonly sha256: string;
}

export interface ManifestSource {
  readonly path: string;
  readonly sha256: string;
  readonly rows: number;
  readonly fixesApplied: number;
}

export interface Manifest {
  readonly schemaVersion: number;
  readonly contentVersion: string;
  readonly generatedAt: string;
  readonly source: ManifestSource;
  readonly files: readonly ManifestFileEntry[];
}

// ── アセットマニフェスト(tools/asset-pipeline の出力)────────────

/** `asset-manifest.json` / `quiz-asset-manifest.json`。エントリの中身は asset-pipeline が決める。 */
export interface AssetManifestFile {
  readonly schemaVersion: number;
  readonly updatedAt: string;
  readonly entries: Readonly<Record<string, unknown>>;
}

// ── 配信パス ─────────────────────────────────────────────

export const MANIFEST_PATH = 'manifest.json';
export const CATALOG_ITEMS_PATH = 'catalog/items.json';
export const CATALOG_GACHAS_PATH = 'catalog/gachas.json';
export const CATALOG_SUBJECTS_PATH = 'catalog/subjects.json';
export const CATALOG_CHARACTERS_PATH = 'catalog/characters.json';
export const CATALOG_RETIREMENTS_PATH = 'catalog/retirements.json';

export function wordsPath(set: string): string {
  return `words/${set}.json`;
}

export function stagesPath(language: StageLanguage): string {
  return `stages/${language}.json`;
}

export function quizBankPath(band: string): string {
  return `quiz/${band}.json`;
}

export function learnPackFilePath(curriculum: string): string {
  return `learn/${curriculum}/pack.json`;
}
